import { ContentLink, useContentSingletons, useXcoreCore } from "@robe/client";
import { Box, Button, Flex, Link as UILink, Modal, Stack, Text, useModal, useTheme } from "@xcorejs/ui";
import regions from "country-region-data";
import { Checkbox, FormControl, Input, Textarea } from "design-system/xcore/Form";
import Select, { AsyncSelect, OptionType } from "design-system/xcore/Select";
import { ChangeEvent, FormEvent, useState } from "react";
import { useLayout } from "xcore";
import { Inquiry, Product } from "xcore/types";
import RobeLoader from "../RobeLoader";
import { useRouter } from "next/router";
import Richtext from "../../design-system/Richtext";
import { ErrorAnimatedIcon, SuccessAnimatedIcon } from "../Icons/AnimatedIcon";
import { mapAndSortProductFilterOptions } from "components/News";
import { useMediaQuery } from "hooks/useMediaQuery";
import { usePdfLambda } from "hooks";

interface InquiryRequest {
  email: string;
  region: null | { value: string; label: string };
  firstName: string;
  lastName: string;
  company: string;
  position: string;
  phone: string;
  note: string;
  city: string;
}

interface InquiryModalProps {
  product: Product;
}

type SentStateType = "idle" | "sent" | string;

export const InquiryModal = ({ product }: InquiryModalProps) => {
  const { stringify } = useLayout();
  const [sentState, setSentState] = useState<SentStateType>("idle");
  const [, inquiry] = useContentSingletons<[any, Inquiry]>();

  if (!inquiry) {
    return (
      <Modal>
        <Flex justifyContent="center" width="100%">
          <RobeLoader />
        </Flex>
      </Modal>
    );
  }

  return (
    <Modal
      header={<Flex flexDirection="column">{stringify(inquiry.values.titleInq)}</Flex>}
      _header={{ justifyContent: "center" }}
      size="lg"
      _overlay={{
        zIndex: 5,
      }}
    >
      {sentState === "idle" ? (
        <InquiryForm inquiry={inquiry} product={product} setSentState={setSentState} />
      ) : (
        <FormResult sentState={sentState} />
      )}
    </Modal>
  );
};

interface InquiryFormProps {
  inquiry: Inquiry;
  product: Product;
  setSentState: (v: SentStateType) => void;
}

const InquiryForm = ({ product, inquiry, setSentState }: InquiryFormProps) => {
  const core = useXcoreCore();
  const { locale } = useRouter();
  const { stringify, header, general } = useLayout();
  const [close] = useModal();
  const theme = useTheme();
  const [isSmScreenWidth] = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const [isMdScreenWidth] = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const datasheetUrl = usePdfLambda({ id: product.id, locale, type: "product" });

  const [products, setProducts] = useState<OptionType[]>([{ label: stringify(product.title), value: product.id }]);
  const [value, setValue] = useState<InquiryRequest>({
    email: "",
    region: null,
    firstName: "",
    lastName: "",
    company: "",
    position: "",
    phone: "",
    note: "",
    city: "",
  });
  const [checkbox, setCheckbox] = useState(false);
  const [sending, setSending] = useState(false);
  const [isRequiredRegionFieldError, setIsRequiredRegionFieldError] = useState(false);

  const handler = <T extends keyof InquiryRequest>(name: T) => ({
    value: value[name],
    onChange: (e: ChangeEvent<HTMLInputElement>) => setValue({ ...value, [name]: e.target.value }),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { region, company, position, note, ...rest } = value;

    if (!region?.value) {
      setIsRequiredRegionFieldError(true);

      return;
    }

    setSending(true);

    core.inquiries
      .insert({
        ...rest,
        ...(company && company !== "" && { company }),
        ...(position && position !== "" && { position }),
        ...(note && note !== "" && { note }),
        contentIds: products.map((p) => p.value) as number[],
        region: region.value,
        locale: locale ?? "en",
      })
      .then(() => {
        setSentState("sent");
        setIsRequiredRegionFieldError(false);
      })
      .catch(() => setSentState(stringify(inquiry.values.inquiryErrorMessage)));
  };

  const loadOptions = async (val: string) => {
    const res = await core.content.search(val, ["product"], { searchInTitle: true });
    if ("product" in res && res?.product?.items.length > 0) {
      return mapAndSortProductFilterOptions((res?.product?.items as Product[]) ?? [], stringify);
    }

    return [];
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box id="inquiry-modal" mt="3rem">
        <Stack direction="column" gap="2rem">
          <Stack direction={{ _: "column", sm: "row" }}>
            <FormControl labelText={stringify(inquiry.values.productsInq)} context="isRequired" width="100%">
              <AsyncSelect
                required
                isMulti
                isSelectInModal
                value={products}
                loadOptions={loadOptions}
                onChange={(v) => (v ? (Array.isArray(v) ? setProducts(v) : setProducts([v as any])) : setProducts([]))}
                // There is a z-index issue while rendering select inside a modal (it is covered by parent elements regardless the z-index). Because of it the portalTarget and styles has to be on this level
                menuPortalTarget={document.getElementById("inquiry-modal")}
                styles={{
                  menuPortal: (styles: any) => ({
                    ...styles,
                    zIndex: 999999,
                    top: "21.4rem",
                    left: isSmScreenWidth ? "2rem" : "11rem",
                    width: isSmScreenWidth ? "90%" : isMdScreenWidth ? "72%" : "73.4%",
                  }),
                }}
              />
            </FormControl>
          </Stack>

          <Stack direction={{ _: "column", sm: "row" }} gap="2rem">
            <FormControl labelText={stringify(inquiry.values.firstNameInq)} context="isRequired" width="100%">
              <Input inputSize="md" width="100%" required {...handler("firstName")} />
            </FormControl>

            <FormControl labelText={stringify(inquiry.values.lastNameInq)} context="isRequired" width="100%">
              <Input inputSize="md" width="100%" required {...handler("lastName")} />
            </FormControl>
          </Stack>

          <Stack direction={{ _: "column", sm: "row" }} gap="2rem">
            <FormControl labelText={stringify(inquiry.values.mailInq)} context="isRequired" width="100%">
              <Input inputSize="md" width="100%" required type="email" {...handler("email")} />
            </FormControl>

            <FormControl labelText={stringify(inquiry.values.phoneNumber)} context="isRequired" width={{ _: "100%", md: "30.5rem" }}>
              <Input inputSize="md" width="100%" required {...handler("phone")} />
            </FormControl>
          </Stack>

          <Stack direction={{ _: "column", sm: "row" }} gap="2rem">
            <FormControl
              labelText={stringify(inquiry.values.regionSelectInq)}
              context="isRequired"
              width="100%"
              {...(isRequiredRegionFieldError && { error: stringify(general.values.requiredField) })}
            >
              <Select
                name="language"
                isSelectInModal
                options={options}
                isMulti={false}
                value={value.region}
                onChange={(v) => {
                  setValue((val) => ({ ...val, region: v as any }));
                  setIsRequiredRegionFieldError(false);
                }}
                // There is a z-index issue while rendering select inside a modal (it is covered by parent elements regardless the z-index). Because of it the portalTarget and styles has to be on this level
                menuPortalTarget={document.getElementById("inquiry-modal")}
                styles={{
                  menuPortal: (styles) => ({
                    ...styles,
                    zIndex: 999999,
                    top: isSmScreenWidth ? "76.8rem" : "55.3rem",
                    left: isSmScreenWidth ? "2rem" : "11rem",
                  }),
                }}
              />
            </FormControl>

            <FormControl labelText={stringify(inquiry.values.cityInq)} width="100%">
              <Input inputSize="md" width="100%" {...handler("city")} />
            </FormControl>
          </Stack>

          <Stack direction={{ _: "column", sm: "row" }} gap="2rem">
            <FormControl labelText={stringify(inquiry.values.companyInq)} width="100%">
              <Input inputSize="md" width="100%" {...handler("company")} />
            </FormControl>

            <FormControl labelText={stringify(inquiry.values.jobPos)} width="100%">
              <Input inputSize="md" width="100%" {...handler("position")} />
            </FormControl>
          </Stack>

          <Stack direction={{ _: "column", sm: "row" }} gap="2rem">
            <FormControl labelText={stringify(inquiry.values.note)} width="100%">
              <Textarea
                inputSize="md"
                width="100%"
                placeholder={stringify(inquiry.values.notePlaceholder)}
                minHeight={{ _: "16rem", md: "8rem" }}
                {...handler("note")}
              />
            </FormControl>
          </Stack>

          <Checkbox size="md" checked={checkbox} onChange={() => setCheckbox((v) => !v)}>
            <Richtext value={inquiry.values.checkboxInq} />
          </Checkbox>

          <Flex flexDirection={{ _: "column", sm: "row" }} justifyContent="space-between" alignItems="center">
            <Button disabled={!checkbox || !products.length || sending} whiteSpace="nowrap">
              {stringify(inquiry.values.sendInq)}
            </Button>

            <Stack gap="6rem" mt={{ _: "3rem", sm: 0 }} ml="2rem">
              <Text variant="strong" fontWeight={600} whiteSpace="nowrap">
                {header.nav.offices.values.officeList?.[0].offices?.[0] &&
                  header.nav.offices.values.officeList?.[0].offices?.[0].type &&
                  header.nav.offices.values.officeList?.[0].offices?.[0].slug && (
                  <ContentLink
                    content={{
                      id: header.nav.offices.values.officeList?.[0].offices?.[0].id,
                      type: header.nav.offices.values.officeList?.[0].offices?.[0].type,
                      slug: header.nav.offices.values.officeList?.[0].offices?.[0].slug,
                    }}
                    onClick={close}
                  >
                    {stringify(inquiry.values.contactBtn)}
                  </ContentLink>
                )}
              </Text>
              {datasheetUrl && (
                <Text variant="strong" fontWeight={600} whiteSpace="nowrap">
                  <UILink href={datasheetUrl} target="_blank">
                    {stringify(inquiry.values.downloadBtn)}
                  </UILink>
                </Text>
              )}
            </Stack>
          </Flex>
        </Stack>
      </Box>
    </form>
  );
};

const options = regions.map((r) => ({ label: r.countryName, value: r.countryShortCode }));

interface FormResultProps {
  sentState: SentStateType;
}

const FormResult = ({ sentState }: FormResultProps) => {
  const { stringify } = useLayout();
  const [, modals] = useContentSingletons<[any, Inquiry]>();

  return (
    <>
      {sentState === "sent" ? (
        <Flex flexDirection="column" alignItems="center" mt="4rem">
          <SuccessAnimatedIcon />

          <Text mt="3rem">{stringify(modals.values.inquirySuccessMessage)}</Text>
        </Flex>
      ) : (
        <Flex flexDirection="column" alignItems="center" mt="4rem">
          <ErrorAnimatedIcon />

          <Text mt="3rem">{sentState}</Text>
        </Flex>
      )}
    </>
  );
};
